/**
 * Cupons de desconto (Plano Loja/Twotrees, Fase 10).
 *
 * Mesmo padrão de `MarketingBanners.ts`/`MarketingFaixaPresale.ts`: View pura
 * testável + submit controlado sem libs extras. Diferença de propósito: não
 * há toggle master — cada cupom já nasce com seu próprio `active`, e não há
 * remoção (`RecordTable` + ação "Desativar", nunca "Remover"): um cupom
 * usado tem pedidos que dependem dele para a proveniência do desconto
 * (checkout-runtime/migration 0047 recusam DELETE de propósito).
 *
 * `usedCount` é só leitura (derivado dos resgates) — a tela nunca o edita.
 *
 * Migrado para `.tsx`/JSX real (Cluster A — Vendas, redesign por perfil; ver
 * `docs/architecture/ADR-0011-*.md`): troca de sintaxe (`createElement` → JSX)
 * + ícones lucide no título e no selo de situação, no mesmo padrão visual de
 * `KpiCard.tsx`/`Margens.tsx` (ícone + texto, nunca só cor — régua §2). Nenhum
 * `data-*`/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `Margens.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { CheckCircle2, Circle, Ticket } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { formatBrl, formatDateTime } from "../format.js";
import { useAddCoupon, useCoupons, useUpdateCoupon } from "../queries.js";
import type { Coupon, CouponDiscountType } from "../api/types.js";

const CONTROL =
  "w-full rounded-control border border-border-strong bg-surface px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted focus-visible:outline-none aria-[invalid=true]:border-danger";

export interface CouponEditHandlers {
  add: (input: {
    code: string;
    discountType: CouponDiscountType;
    discountValue: number;
    minSubtotalCents: number | null;
    maxUses: number | null;
    maxUsesPerCustomer: number | null;
  }) => void;
  toggleActive: (coupon: Coupon) => void;
  pending: boolean;
  error: Error | null;
}

export interface CuponsViewProps {
  coupons: Coupon[];
  edit?: CouponEditHandlers;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface CouponFormValues {
  get(name: string): unknown;
}

function parseOptionalInt(raw: unknown): number | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Caminho de submit REAL do formulário de criar cupom, isolado do evento de
 * DOM. Devolve o input enviado, ou `null` quando a validação recusa antes de
 * chamar o servidor (código/valor inválidos) — mesma convenção de
 * `submitAddBannerForm`.
 */
export function submitAddCouponForm(
  values: CouponFormValues,
  edit: CouponEditHandlers
): { code: string; discountType: CouponDiscountType; discountValue: number; minSubtotalCents: number | null; maxUses: number | null; maxUsesPerCustomer: number | null } | null {
  const code = values.get("code");
  const discountTypeRaw = values.get("discountType");
  const discountValueRaw = values.get("discountValue");
  if (typeof code !== "string" || !code.trim()) return null;
  if (discountTypeRaw !== "percentage" && discountTypeRaw !== "fixed") return null;
  const discountType: CouponDiscountType = discountTypeRaw;
  const discountValue = typeof discountValueRaw === "string" ? Number(discountValueRaw) : NaN;
  if (!Number.isInteger(discountValue) || discountValue <= 0) return null;
  if (discountType === "percentage" && discountValue > 100) return null;

  const minSubtotalRaw = values.get("minSubtotalCents");
  const minSubtotalReais = typeof minSubtotalRaw === "string" && minSubtotalRaw.trim() ? Number(minSubtotalRaw) : null;
  const minSubtotalCents = minSubtotalReais !== null && Number.isFinite(minSubtotalReais) && minSubtotalReais > 0 ? Math.round(minSubtotalReais * 100) : null;

  const input = {
    code: code.trim().toUpperCase(),
    discountType,
    discountValue,
    minSubtotalCents,
    maxUses: parseOptionalInt(values.get("maxUses")),
    maxUsesPerCustomer: parseOptionalInt(values.get("maxUsesPerCustomer"))
  };
  edit.add(input);
  return input;
}

function discountLabel(coupon: Coupon): string {
  return coupon.discountType === "percentage" ? `${coupon.discountValue}%` : formatBrl(coupon.discountValue);
}

function usageLabel(coupon: Coupon): string {
  const used = `${coupon.usedCount} uso${coupon.usedCount === 1 ? "" : "s"}`;
  return coupon.maxUses !== null ? `${used} de ${coupon.maxUses}` : used;
}

function CouponActions({ coupon, edit }: { coupon: Coupon; edit?: CouponEditHandlers }): ReactElement | null {
  if (!edit) return null;
  const BTN =
    "inline-flex min-h-[32px] items-center justify-center rounded-control border border-border-strong px-2 text-xs font-semibold text-body hover:bg-surface-soft disabled:opacity-50";
  return (
    <button
      type="button"
      className={BTN}
      disabled={edit.pending}
      data-action={`coupon-toggle-${coupon.id}`}
      onClick={() => edit.toggleActive(coupon)}
    >
      {coupon.active ? "Desativar" : "Ativar"}
    </button>
  );
}

/** Selo de situação do cupom: ícone + texto, nunca só cor (régua §2) — mesmo padrão de `MarginStatusBadge` em `Margens.tsx`. */
function CouponActiveBadge({ coupon }: { coupon: Coupon }): ReactElement {
  const Icon = coupon.active ? CheckCircle2 : Circle;
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
        (coupon.active ? "border-success text-success" : "border-border-strong text-body")
      }
      data-coupon-active={coupon.active ? "yes" : "no"}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{coupon.active ? "ativo" : "inativo"}</span>
    </span>
  );
}

export function CuponsView({ coupons, edit }: CuponsViewProps): ReactElement {
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edit) return;
    const form = event.currentTarget;
    const result = submitAddCouponForm(new FormData(form), edit);
    if (result) form.reset();
  }

  return (
    <div className="page page--coupons flex flex-col gap-6" data-page="coupons">
      <header className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-2xl font-extrabold text-ink">
          <Ticket className="h-5 w-5 shrink-0 text-brand-text" aria-hidden focusable={false} />
          Cupons de desconto
        </h2>
        <p className="page__note text-sm text-muted">
          Cadastre cupons para o checkout da Loja. O desconto é travado no pedido no momento da compra — mudar ou desativar um cupom
          nunca altera pedidos já feitos.
        </p>
      </header>
      <RecordTable<Coupon>
        caption="Cupons cadastrados"
        rows={coupons}
        idOf={(row) => row.id}
        emptyMessage="Nenhum cupom cadastrado ainda."
        columns={[
          { key: "code", label: "Código", render: (row) => <strong className="font-mono">{row.code}</strong> },
          { key: "discount", label: "Desconto", render: (row) => discountLabel(row) },
          { key: "minSubtotal", label: "Pedido mínimo", render: (row) => (row.minSubtotalCents !== null ? formatBrl(row.minSubtotalCents) : "—") },
          { key: "usage", label: "Uso", render: (row) => usageLabel(row) },
          { key: "endsAt", label: "Expira em", render: (row) => (row.endsAt ? formatDateTime(row.endsAt) : "—") },
          { key: "active", label: "Situação", render: (row) => <CouponActiveBadge coupon={row} /> },
          { key: "actions", label: "Ações", render: (row) => <CouponActions coupon={row} edit={edit} /> }
        ]}
      />
      {edit ? (
        <form
          className="coupon-add-form flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
          data-form="add-coupon"
          onSubmit={onSubmit}
        >
          <h4 className="text-sm font-semibold text-ink">Adicionar cupom</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-code" className="text-sm font-semibold text-ink">
                Código
              </label>
              <input
                id="coupon-code"
                name="code"
                type="text"
                required
                disabled={edit.pending}
                className={CONTROL}
                placeholder="BEMVINDO10"
                data-field="code"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-discount-type" className="text-sm font-semibold text-ink">
                Tipo
              </label>
              <select
                id="coupon-discount-type"
                name="discountType"
                required
                disabled={edit.pending}
                className={CONTROL}
                data-field="discountType"
                defaultValue="percentage"
              >
                <option value="percentage">Percentual (%)</option>
                <option value="fixed">Valor fixo (R$)</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-discount-value" className="text-sm font-semibold text-ink">
                Valor
              </label>
              <input
                id="coupon-discount-value"
                name="discountValue"
                type="number"
                min={1}
                required
                disabled={edit.pending}
                className={CONTROL}
                placeholder="10"
                data-field="discountValue"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-min-subtotal" className="text-sm font-semibold text-ink">
                Pedido mínimo (R$, opcional)
              </label>
              <input
                id="coupon-min-subtotal"
                name="minSubtotalCents"
                type="number"
                min={0}
                step="0.01"
                disabled={edit.pending}
                className={CONTROL}
                placeholder="200,00"
                data-field="minSubtotalCents"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-max-uses" className="text-sm font-semibold text-ink">
                Limite total de usos (opcional)
              </label>
              <input
                id="coupon-max-uses"
                name="maxUses"
                type="number"
                min={1}
                disabled={edit.pending}
                className={CONTROL}
                placeholder="Sem limite"
                data-field="maxUses"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-max-uses-per-customer" className="text-sm font-semibold text-ink">
                Limite por cliente (opcional)
              </label>
              <input
                id="coupon-max-uses-per-customer"
                name="maxUsesPerCustomer"
                type="number"
                min={1}
                disabled={edit.pending}
                className={CONTROL}
                placeholder="Sem limite"
                data-field="maxUsesPerCustomer"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={edit.pending}
            data-action="add-coupon"
            className={
              "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control bg-brand px-4 py-2 " +
              "text-sm font-semibold text-on-brand transition-colors duration-fast ease-s2 " +
              "hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-70"
            }
          >
            {edit.pending ? "Gravando…" : "Adicionar cupom"}
          </button>
        </form>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Gravação de cupom não montada nesta visualização"
          detail="A lista acima reflete o banco; esta montagem é só leitura."
        />
      )}
      {edit?.error ? <StatusPanel kind="error" title="O cupom não foi atualizado" detail={edit.error.message} /> : null}
    </div>
  );
}

export function Cupons(): ReactElement {
  const couponsQuery = useCoupons();
  const addCoupon = useAddCoupon();
  const updateCoupon = useUpdateCoupon();

  if (couponsQuery.isPending) {
    return <StatusPanel kind="loading" title="Carregando cupons…" />;
  }
  if (couponsQuery.error) {
    return <StatusPanel kind="error" title="Não foi possível carregar os cupons" detail={errorDetail(couponsQuery.error)} />;
  }

  const pending = addCoupon.isPending || updateCoupon.isPending;
  const error = addCoupon.error ?? updateCoupon.error ?? null;

  return (
    <CuponsView
      coupons={couponsQuery.data ?? []}
      edit={{
        add: (input) => addCoupon.mutate(input),
        toggleActive: (coupon) => updateCoupon.mutate({ id: coupon.id, patch: { active: !coupon.active } }),
        pending,
        error
      }}
    />
  );
}
